import React, { useEffect, useRef, useState } from "react";
import { Input, InputNumber, Select, DatePicker, Popover } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { FilterOutlined } from "@ant-design/icons";
import { DebounceSelect } from "../DataEntry/Select/DebounceSelect";

const { RangePicker } = DatePicker;

/* ========= Types ========= */

export type FilterType = "string" | "number" | "select" | "dateRange";

export type StringFilterConfig = {
  type: "string";
  filterProps?: React.ComponentProps<typeof Input>;
};

export type NumberFilterConfig = {
  type: "number";
  filterProps?: React.ComponentProps<typeof InputNumber>;
};

export type SelectFilterConfig = {
  type: "select";
  filterProps: React.ComponentProps<typeof Select>;
};
export type DebounceSelectFilterConfig = {
  type: "debounce";
  filterProps: React.ComponentProps<typeof Select> | any;
};

export type DateRangeFilterConfig = {
  type: "dateRange";
  filterProps?: React.ComponentProps<typeof RangePicker>;
};

export type FilterConfig =
  | StringFilterConfig
  | NumberFilterConfig
  | SelectFilterConfig
  | DebounceSelectFilterConfig
  | DateRangeFilterConfig;

export type FiltersState = Record<string, any>;
type HeaderFilterOptions = {
  popoverProps?: React.ComponentProps<typeof Popover>;
};

// Column của antd + cấu hình filter
export type ColumnWithFilter<T> = ColumnType<T> & {
  // Dùng làm key filter khi dataIndex là nested path hoặc cần map sang API param khác.
  filterDataIndex?: string;
  filterConfig?: FilterConfig;
};

/* ========= Helper: build columns with header filter ========= */

type StringFilterInputProps = {
  value?: string;
  onDebouncedChange: (value: string) => void;
  inputProps?: React.ComponentProps<typeof Input>;
  style?: React.CSSProperties;
  delay?: number;
};

export const StringFilterInput: React.FC<StringFilterInputProps> = ({
  value,
  onDebouncedChange,
  inputProps,
  style,
  delay = 1000,
}) => {
  const [innerValue, setInnerValue] = useState(value ?? "");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCallbackRef = useRef(onDebouncedChange);

  useEffect(() => {
    latestCallbackRef.current = onDebouncedChange;
  }, [onDebouncedChange]);

  useEffect(() => {
    setInnerValue(value ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Input
      {...inputProps}
      style={style}
      value={innerValue}
      onChange={(e) => {
        const nextValue = e.target.value;
        setInnerValue(nextValue);

        if (timerRef.current) clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
          latestCallbackRef.current(nextValue);
        }, delay);
      }}
    />
  );
};
export function buildColumnsWithHeaderFilters<T>(
  baseColumns: ColumnWithFilter<T>[],
  filters: FiltersState,
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>,
  options?: HeaderFilterOptions,
): ColumnsType<T> {
  const handleChange = (key: string, value: any) => {
    setFilters((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };
  // const handleChangeTimeout = ()

  const renderFilterControl = (col: ColumnWithFilter<T>) => {
    const cfg = col.filterConfig!;
    const key = col?.filterDataIndex ?? String(col.dataIndex);
    const value = filters[key];
    const style = { width: "100%", ...(cfg.filterProps?.style || {}) };

    switch (cfg.type) {
      case "string":
        return (
          <StringFilterInput
            inputProps={cfg.filterProps}
            style={style}
            value={value}
            delay={500}
            onDebouncedChange={(nextValue) => handleChange(key, nextValue)}
          />
        );

      case "number":
        return (
          <InputNumber
            {...cfg.filterProps}
            style={style}
            value={value}
            onChange={(v) => handleChange(key, v)}
          />
        );

      case "select":
        return (
          <Select
            {...cfg.filterProps}
            style={style}
            value={value}
            onChange={(v) => handleChange(key, v)}
            allowClear
          />
        );
      case "debounce":
        return (
          <DebounceSelect
            {...cfg.filterProps}
            style={style}
            value={value}
            onChange={(v) => handleChange(key, v?.value)}
            allowClear
          />
        );

      case "dateRange":
        return (
          <RangePicker
            {...cfg.filterProps}
            style={style}
            value={value as [Dayjs, Dayjs] | null}
            onChange={(v) => handleChange(key, v)}
            allowClear
          />
        );

      default:
        return null;
    }
  };

  const hasFilterValue = (col: ColumnWithFilter<T>): boolean => {
    const cfg = col.filterConfig!;
    const key = String(col?.filterDataIndex ?? col.dataIndex);
    const value = filters[key];

    switch (cfg.type) {
      case "string":
      case "number":
      case "select":
        return value !== undefined && value !== null && value !== "";
      case "debounce":
        return value !== undefined && value !== null && value !== "";
      case "dateRange":
        return (
          Array.isArray(value) && value.length === 2 && value[0] && value[1]
        );
      default:
        return false;
    }
  };

  // ✅ Đệ quy build columns (bao gồm children)
  const build = (cols: any[]): any[] => {
    return cols.map((col: any) => {
      // nếu có children -> build đệ quy cho children trước
      if (Array.isArray(col?.children) && col.children.length > 0) {
        return {
          ...col,
          children: build(col.children),
        };
      }

      // không có filterConfig -> trả nguyên
      if (!col?.filterConfig) return col;

      const active = hasFilterValue(col as ColumnWithFilter<T>);

      return {
        ...col,
        title: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            <span>{col.title}</span>

            <Popover
              trigger="click"
              placement="topRight"
              {...options?.popoverProps}
              content={
                <div style={{ minWidth: 200, padding: 4 }}>
                  {renderFilterControl(col as ColumnWithFilter<T>)}
                </div>
              }
            >
              <span
                onClick={(e) => e.stopPropagation()}
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <FilterOutlined
                  style={{
                    fontSize: 14,
                    color: active ? "#1677ff" : "#999",
                  }}
                />
              </span>
            </Popover>
          </div>
        ),
      };
    });
  };

  return build(baseColumns) as ColumnsType<T>;
}
/* ========= Helper: apply filters to data ========= */

export function applyFilters<T>(
  rows: T[],
  filters: FiltersState,
  columns: ColumnWithFilter<T>[],
): T[] {
  return rows.filter((row) => {
    for (const col of columns) {
      const cfg = col.filterConfig;
      if (!cfg) continue;

      const key = col.dataIndex as string;
      const filterValue = filters[key];

      // Không cấu hình filter hoặc chưa nhập gì -> bỏ qua
      if (
        filterValue === undefined ||
        filterValue === null ||
        filterValue === ""
      ) {
        continue;
      }

      const cellValue = (row as any)[key];

      switch (cfg.type) {
        case "string":
        case "number": {
          const cellStr = String(cellValue ?? "").toLowerCase();
          const filterStr = String(filterValue ?? "").toLowerCase();
          if (!cellStr.includes(filterStr)) return false;
          break;
        }

        case "select": {
          if (cellValue !== filterValue) return false;
          break;
        }

        case "dateRange": {
          const [start, end] = filterValue as [Dayjs, Dayjs];
          if (!start || !end) break;

          const d = dayjs(cellValue);
          if (!d.isValid()) break;

          if (d.isBefore(start, "day") || d.isAfter(end, "day")) return false;
          break;
        }

        default:
          break;
      }
    }

    return true;
  });
}
